import type { CustomSeriesOption, CustomSeriesRenderItemAPI, CustomSeriesRenderItemParams } from 'echarts';

import type { TimeRangeLane } from '@/shared/charts/time-series-chart/TimeSeriesChart.types';
import type { TimeDomain } from '../utils/timeDomain';
import { getTimeRangeLaneColor } from '@/shared/charts/time-series-chart/theme/getTimeRangeLaneColor';
import type { TimeSeriesChartTheme } from '@/shared/charts/time-series-chart/theme/TimeSeriesChartTheme.types';

import { chartLayout } from '../config/chartLayout';
import { highlightVisuals } from '../config/chartVisuals';
import { getRowHighlightSeriesId } from '../config/componentIds';
import { toTranslucentColor } from '../utils/toTranslucentColor';
import { getLaneHeight } from './getLaneHeight';

const LANE_DIMENSION = 0;

type RowHighlightItem = [number];

type PlotRect = {
  x: number;
  width: number;
};

type BuildRowHighlightOptionParams = {
  lane: TimeRangeLane;
  laneIndex: number;
  domain: TimeDomain;
  xAxisIndex: number;
  yAxisIndex: number;
  theme: TimeSeriesChartTheme;
};

function buildRowStyle(lane: TimeRangeLane, theme: TimeSeriesChartTheme) {
  const color = getTimeRangeLaneColor(lane, theme);

  return {
    fill: toTranslucentColor(color, highlightVisuals.fillOpacity),
    stroke: color,
    lineWidth: highlightVisuals.borderWidth,
  };
}

/** `params.coordSys` is untyped in ECharts; only the plot rectangle is needed and it is validated here. */
function getPlotRect(params: CustomSeriesRenderItemParams): PlotRect | undefined {
  const coordSys: unknown = params.coordSys;

  if (typeof coordSys !== 'object' || coordSys === null || !('x' in coordSys) || !('width' in coordSys)) {
    return undefined;
  }

  const { x, width } = coordSys;

  if (typeof x !== 'number' || typeof width !== 'number') {
    return undefined;
  }

  return { x, width };
}

function createRenderItem(lane: TimeRangeLane, domain: TimeDomain, theme: TimeSeriesChartTheme) {
  const style = buildRowStyle(lane, theme);

  return function renderRowHighlight(params: CustomSeriesRenderItemParams, api: CustomSeriesRenderItemAPI) {
    const plotRect = getPlotRect(params);

    if (!plotRect) {
      return undefined;
    }

    const laneIndex = api.value(LANE_DIMENSION);
    const [, centerY] = api.coord([domain[0], laneIndex]);
    const height = getLaneHeight(api) * highlightVisuals.rowHeightRatio;

    return {
      type: 'rect' as const,
      shape: {
        x: chartLayout.rowHighlightLeft,
        y: centerY - height / 2,
        width: plotRect.x + plotRect.width - chartLayout.rowHighlightLeft,
        height,
        r: theme.rangeTrack.rowBorderRadius,
      },
      style,
    };
  };
}

/**
 * Unclipped custom series drawing the highlighted row background. It spans from just left of the
 * grid to its right edge, independent of the zoom window.
 */
export function buildRowHighlightOption({
  lane,
  laneIndex,
  domain,
  xAxisIndex,
  yAxisIndex,
  theme,
}: BuildRowHighlightOptionParams): CustomSeriesOption {
  const data: RowHighlightItem[] = [[laneIndex]];

  return {
    id: getRowHighlightSeriesId(lane.id),
    type: 'custom',
    xAxisIndex,
    yAxisIndex,
    clip: false,
    silent: true,
    renderItem: createRenderItem(lane, domain, theme),
    encode: {
      y: LANE_DIMENSION,
    },
    data,
  };
}
